// The Inbound Referrals page sections (OLIO-3322). Each section is a stored CATEGORY in the
// preference map — the same shape care coordination uses, with a per-section `active` master —
// and each row is a real notification kind the api dispatches on. Keys are the camelized wire
// names. Rows the mock shows that are NOT kinds are deliberately absent here:
//   - variants of one send ("Errored: Auto Check", "Flagged for Review") are captions on the
//     single real kind, not separate rows;
//   - the Patient Identification section is aspirational and ships with no kinds behind it.
// Rows carry no channel key: the section component renders the Email column always and the
// In-App column behind the user's in_app_notifications flag (OLIO-3231), reading both off the
// stored preference values.

export type InboundReferralKind = {
  kind: string;
  label: string;
  caption?: string;
};

export type InboundReferralSection = {
  category: string;
  title: string;
  description: string;
  kinds: InboundReferralKind[];
};

export const INBOUND_REFERRAL_SECTIONS: InboundReferralSection[] = [
  {
    category: 'referralIntake',
    title: 'Referral Intake',
    description: 'New referrals arriving in Olio and flagging if they cannot be processed',
    kinds: [
      { kind: 'newReferral', label: 'New Referral Received' },
      {
        kind: 'missingInformation',
        label: 'Referral Missing Information',
        caption:
          'Referral cannot be processed due to missing information. Note: This will be sent regardless of referral source',
      },
    ],
  },
  {
    category: 'autoChecks',
    title: 'Auto Checks',
    description: 'Results from automated checks run by Olio',
    kinds: [
      {
        kind: 'autoChecks',
        label: 'Auto Check Summary',
        caption: 'Sends a single summary email once auto checks are complete, including any errored or flagged checks',
      },
    ],
  },
  {
    category: 'clinicalAssessments',
    title: 'Clinical Assessment',
    description: 'Results for clinical assessments based on referral documentation',
    kinds: [
      {
        kind: 'clinicalAssessments',
        label: 'Clinical Assessment Results',
        caption: 'Sent when a clinical assessment completes, is flagged for review, or fails to process',
      },
      { kind: 'newAssessmentNote', label: 'New Clinical Assessment Note' },
      { kind: 'newWorkflowActionMention', label: 'Tagged in Clinical Assessment Note' },
    ],
  },
  {
    category: 'facilityDecision',
    title: 'Facility Decision',
    description: 'Indication whether a facility has approved or denied a referral',
    kinds: [
      { kind: 'facilityAccepted', label: 'Facility Accepted' },
      { kind: 'facilityDenied', label: 'Facility Denied' },
    ],
  },
  {
    category: 'referralOutcome',
    title: 'Referral Outcome',
    description: 'Indication whether a facility has been selected or not selected by the patient',
    kinds: [
      { kind: 'referralWon', label: 'Facility Selected' },
      { kind: 'referralLost', label: 'Facility Not Selected' },
    ],
  },
  {
    category: 'chat',
    title: 'Chat',
    description: 'New chat messages related to a referral',
    kinds: [
      { kind: 'newChatMessage', label: 'New Referral Message' },
      { kind: 'newChatMention', label: 'Tagged in Referral Chat' },
    ],
  },
  // referralTasks is deliberately absent (portal-automation ruling, 2026-09-18): the digest
  // belongs to the Referral Tasks product, gated by its own product access — no preferences row.
];
